import { formattedRole } from '@/renderer/lib/utils';
import { createSelector } from '@reduxjs/toolkit';
import { AccessControlCode, FULL_ACCESS_USER_ROLES } from '@shared/constants';
import { UserRole } from '@shared/types';
import { RootState } from '..';
import { BILLING_PAGE_PATH } from '../../util/renderConstants';

export const selectAuthState = (state: RootState) => state.auth;

export const selectPendingGoogleUser = (state: RootState) =>
  state.auth.pendingGoogleUser;

export const selectIsAuthenticated = (state: RootState) =>
  state.auth.isAuthenticated;

export const selectCurrentUser = (state: RootState) => state.auth.user;

export const selectUserRoleName = createSelector(
  [selectCurrentUser],
  (user) => {
    if (!user?.role) return 'Unknown role';
    return formattedRole(user.role);
  },
);

export const selectAccessControlCodes = (state: RootState) =>
  state.auth.accessControlCodes;

export const selectAuthLoading = (state: RootState) => state.auth.isLoading;

export const selectAuthInitialized = (state: RootState) =>
  state.auth.isInitialized;

export const selectAuthError = (state: RootState) => state.auth.error;

export const selectSubscription = (state: RootState) =>
  state.auth.user?.subscription;

const getSubscriptionCountdownDate = (
  subscription: RootState['auth']['user'] extends infer T
    ? T extends { subscription?: infer S }
      ? S | undefined
      : undefined
    : undefined,
) => {
  if (!subscription) return null;

  if (
    subscription.subscription_status === 'past_due' &&
    subscription.grace_period_ends_at
  ) {
    return subscription.grace_period_ends_at;
  }

  if (
    subscription.subscription_status === 'trialing' &&
    subscription.trial_ends_at
  ) {
    return subscription.trial_ends_at;
  }

  return subscription.current_period_end ?? null;
};

export const selectTrialDaysRemaining = createSelector(
  [selectSubscription],
  (subscription) => {
    const countdownDate = getSubscriptionCountdownDate(subscription);
    if (!countdownDate) return 0;

    const currentPeriod = new Date(countdownDate);
    const now = new Date();
    const diffTime = currentPeriod.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  },
);

export const selectBillingTimeLeftLabel = createSelector(
  [selectTrialDaysRemaining],
  (daysLeft) => {
    if (daysLeft < 30) {
      return `${daysLeft.toLocaleString()} days left`;
    }

    const monthsLeft = Math.max(1, Math.ceil(daysLeft / 30));
    return `${monthsLeft.toLocaleString()} months left`;
  },
);

export const selectHasActiveSubscription = (state: RootState) =>
  state.auth.user?.subscription?.stripe_customer_id;

export const selectIsSubscriptionSuspended = createSelector(
  [selectSubscription],
  (subscription) => {
    if (!subscription) return false;
    if (subscription.suspended_at) return true;
    if (
      subscription.subscription_status === 'past_due' &&
      subscription.grace_period_ends_at
    ) {
      return (
        new Date(subscription.grace_period_ends_at).getTime() <= Date.now()
      );
    }

    return false;
  },
);

export const selectIsSubscriptionInGracePeriod = createSelector(
  [selectSubscription, selectIsSubscriptionSuspended],
  (subscription, isSuspended) =>
    Boolean(
      subscription?.subscription_status === 'past_due' &&
      subscription.grace_period_ends_at &&
      !isSuspended,
    ),
);

export const selectBillingRecoveryDates = createSelector(
  [selectSubscription],
  (subscription) => ({
    gracePeriodEndsAt: subscription?.grace_period_ends_at || null,
    dataPurgeScheduledFor: subscription?.data_purge_scheduled_for || null,
    lastReminderSentAt: subscription?.last_payment_reminder_sent_at || null,
  }),
);

export const selectDaysUntilRenewal = createSelector(
  [selectSubscription],
  (subscription): number | null => {
    if (!subscription?.current_period_end) return null;
    if (!['active', 'trialing'].includes(subscription.subscription_status))
      return null;
    const end = new Date(subscription.current_period_end).getTime();
    const diffDays = Math.ceil((end - Date.now()) / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays : null;
  },
);

export const selectIsSubscriptionRenewingSoon = createSelector(
  [selectDaysUntilRenewal],
  (daysUntilRenewal) => daysUntilRenewal !== null && daysUntilRenewal <= 10,
);

export const selectUserOnboarding = (state: RootState) =>
  state.auth.user?.onboarding;

export const selectHasCompletedFirstTimeOnboarding = createSelector(
  [selectUserOnboarding],
  (onboarding) => onboarding?.status === 'completed',
);

export const selectNeedsFirstTimeOnboarding = createSelector(
  [
    selectCurrentUser,
    selectHasActiveSubscription,
    selectHasCompletedFirstTimeOnboarding,
  ],
  (user, hasActiveSubscription, hasCompletedFirstTimeOnboarding) =>
    Boolean(
      user &&
      hasActiveSubscription &&
      user.company?.created_by === user.id &&
      !hasCompletedFirstTimeOnboarding,
    ),
);

export const selectUserRole = (state: RootState) => state.auth.user?.role;

export const selectHasUnrestrictedAccess = createSelector(
  [selectUserRole],
  (role) => Boolean(role && FULL_ACCESS_USER_ROLES.includes(role as UserRole)),
);

export const selectHasAccessControl = (code: AccessControlCode | string) =>
  createSelector(
    [selectHasUnrestrictedAccess, selectAccessControlCodes],
    (hasUnrestrictedAccess, accessControlCodes) =>
      hasUnrestrictedAccess || accessControlCodes.includes(code),
  );

export const selectCompanyId = (state: RootState) =>
  state.auth.user?.company?.id;

export const selectUserCountryId = (state: RootState) =>
  state.auth.user?.company?.country_id ?? null;

export const selectUserCurrencyPrefix = (state: RootState) =>
  state.auth.user?.company?.currency_prefix ||
  state.auth.user?.outlet?.currency_prefix ||
  '';

export const selectOutletId = (state: RootState) => state.auth.user?.outlet?.id;

export const hasSetupBilling = (state: RootState) =>
  state.auth.user?.subscription?.stripe_customer_id;

export const selectIsUserActive = (state: RootState) =>
  state.auth.user?.is_active ?? false;

export const selectCanUseCompanyScope = selectHasAccessControl(
  AccessControlCode.DASHBOARD_MULTIPLE_OUTLETS_ACCESS,
);

export const selectUserOutlet = (state: RootState) => state.auth.user?.outlet;

// Access controls
export const selectCanOpenDashboardShell = createSelector(
  [selectHasUnrestrictedAccess, selectAccessControlCodes],
  (hasUnrestrictedAccess, accessControlCodes) =>
    hasUnrestrictedAccess ||
    accessControlCodes.some((code) => code.startsWith('dashboard.')),
);

export const selectCanAccessDashboard = selectHasAccessControl(
  AccessControlCode.DASHBOARD_PAGE_ACCESS,
);

export const selectPreferredDashboardRoute = createSelector(
  [selectHasUnrestrictedAccess, selectAccessControlCodes, selectUserRole],
  (hasUnrestrictedAccess, accessControlCodes, userRole) => {
    if (hasUnrestrictedAccess) {
      return '/dashboard';
    }

    if (accessControlCodes.includes(AccessControlCode.DASHBOARD_PAGE_ACCESS)) {
      return '/dashboard';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOMMODATION_PAGE_ACCESS)
    ) {
      return '/accommodation/dashboard';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOUNTING_VIEW_REPORTS)
    ) {
      return '/accounting/dashboard';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOUNTING_VIEW_ACCOUNTS)
    ) {
      return '/accounting/chart-of-accounts';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOUNTING_VIEW_JOURNALS)
    ) {
      return '/accounting/journals';
    }

    if (
      accessControlCodes.includes(
        AccessControlCode.ACCOUNTING_MANAGE_PAYMENT_MAPPINGS,
      )
    ) {
      return '/accounting/settings/payment-mappings';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOUNTING_MANAGE_TAX_RATES)
    ) {
      return '/accounting/settings/tax-rates';
    }

    if (
      accessControlCodes.includes(AccessControlCode.ACCOUNTING_CLOSE_PERIODS)
    ) {
      return '/accounting/settings/accounting-periods';
    }

    // Receptionists go straight to the accommodation module
    if (userRole === UserRole.RECEPTIONIST) {
      return '/accommodation/dashboard';
    }

    // KDS users go straight to the Kitchen Display Screen
    if (userRole === UserRole.KDS) {
      return '/kitchen-display';
    }

    if (userRole === UserRole.SUPER || userRole === UserRole.SUPER_ADMIN) {
      return '/settings/security/access-controls';
    }

    if (
      accessControlCodes.includes(
        AccessControlCode.DASHBOARD_SALES_REPORTS_VIEW,
      )
    ) {
      return '/dashboard/reports/orders';
    }

    if (accessControlCodes.includes(AccessControlCode.DASHBOARD_CLIENTS_VIEW)) {
      return '/dashboard/clients';
    }

    if (accessControlCodes.includes(AccessControlCode.SETTINGS_USERS_MANAGE)) {
      return '/settings/users';
    }

    if (
      accessControlCodes.includes(AccessControlCode.DASHBOARD_SETTINGS_ACCESS)
    ) {
      return '/dashboard/settings';
    }

    if (
      accessControlCodes.includes(AccessControlCode.DASHBOARD_BILLINGS_ACCESS)
    ) {
      return BILLING_PAGE_PATH;
    }

    if (
      accessControlCodes.includes(
        AccessControlCode.DASHBOARD_DEPARTMENTS_CREATE,
      )
    ) {
      return '/dashboard/departments';
    }

    if (accessControlCodes.includes(AccessControlCode.DASHBOARD_MENU_MANAGE)) {
      return '/dashboard/menu';
    }

    if (
      accessControlCodes.includes(
        AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
      )
    ) {
      return '/dashboard/sections-tables';
    }

    if (
      accessControlCodes.includes(AccessControlCode.DASHBOARD_OUTLETS_MANAGE)
    ) {
      return '/dashboard/outlets';
    }

    return null;
  },
);

export const selectCanManageDepartments = selectHasAccessControl(
  AccessControlCode.DASHBOARD_DEPARTMENTS_CREATE,
);

export const selectCanManageMenu = selectHasAccessControl(
  AccessControlCode.DASHBOARD_MENU_MANAGE,
);

export const selectCanManageSectionsTables = selectHasAccessControl(
  AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
);

export const selectCanManageOutlets = selectHasAccessControl(
  AccessControlCode.DASHBOARD_OUTLETS_MANAGE,
);

export const selectCanManageUsers = selectHasAccessControl(
  AccessControlCode.SETTINGS_USERS_MANAGE,
);

export const selectCanManageSecurity = createSelector(
  [selectUserRole],
  (role) => role === UserRole.SUPER || role === UserRole.SUPER_ADMIN,
);

export const selectIsReceptionist = createSelector(
  [selectUserRole],
  (role) => role === UserRole.RECEPTIONIST,
);

export const selectIsKds = createSelector(
  [selectUserRole],
  (role) => role === UserRole.KDS,
);

export const selectOutletHasKdsEnabled = createSelector(
  [(state: RootState) => state.auth.user?.outlet?.is_kds_enabled],
  (enabled) => Boolean(enabled),
);

export const selectHasFiscalIntegration = createSelector(
  [(state: RootState) => state.auth.user?.company?.has_fiscal_integration],
  (enabled) => Boolean(enabled),
);

/** Outlet-level: this outlet's EBM device is activated, so sales must be
 *  fiscalised. Unlike selectHasFiscalIntegration (country-level, controls
 *  whether the EBM setup pages are visible), this gates sales enforcement. */
export const selectOutletIsEbmActive = createSelector(
  [(state: RootState) => state.auth.user?.outlet?.is_ebm_active],
  (active) => Boolean(active),
);
